'use client';

import { useState, useEffect } from 'react';

/**
 * Feature flag configuration
 * Simplified after completing auth system migration
 */

// Feature flag keys - use descriptive names
export enum FeatureFlag {
  // Authentication mechanism flags
  USE_SERVER_AUTH = 'use_server_auth',

  // UI/UX flags
  USE_IMPROVED_HEADER = 'use_improved_header',

  // Safety mechanisms
  USE_FALLBACK_HANDLER = 'use_fallback_handler',
  USE_AUTH_MONITORING = 'use_auth_monitoring',
}

// Feature flag configuration - post-migration
// All auth improvements are now the default implementation
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  // Auth system is server-side by default
  [FeatureFlag.USE_SERVER_AUTH]: true,

  // UI flags
  [FeatureFlag.USE_IMPROVED_HEADER]: true,

  // Safety mechanisms remain enabled
  [FeatureFlag.USE_FALLBACK_HANDLER]: true,
  [FeatureFlag.USE_AUTH_MONITORING]: true,
};

// User-specific feature flags - can override defaults based on user criteria
export interface UserFeatureFlags {
  userId: string;
  flags: Partial<Record<FeatureFlag, boolean>>;
}

// List of users with specific feature flag overrides
const USER_FEATURE_FLAGS: UserFeatureFlags[] = [];

/**
 * Get the value of a feature flag
 * 
 * @param flag The feature flag to check
 * @param userId Optional user ID for user-specific flags
 * @returns The feature flag value (true or false)
 */
export function getFeatureFlag(flag: FeatureFlag, userId?: string): boolean {
  // First check if there's a local storage override
  if (typeof window !== 'undefined') {
    const localOverride = localStorage.getItem(`feature_${flag}`);
    if (localOverride) {
      return localOverride === 'true';
    }
  }
  
  // Then check for user-specific overrides
  if (userId) {
    const userFlags = USER_FEATURE_FLAGS.find(u => u.userId === userId);
    if (userFlags && userFlags.flags[flag] !== undefined) {
      return userFlags.flags[flag]!;
    }
  }
  
  // Finally, use the default value
  return DEFAULT_FLAGS[flag];
}

/**
 * Set a feature flag override in localStorage
 * Useful for testing and development
 * 
 * @param flag The feature flag to set
 * @param value The value to set (true or false)
 */
export function setFeatureFlag(flag: FeatureFlag, value: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(`feature_${flag}`, value.toString());
  }
}

/**
 * Reset a feature flag to its default value
 * 
 * @param flag The feature flag to reset
 */
export function resetFeatureFlag(flag: FeatureFlag): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(`feature_${flag}`);
  }
}

/**
 * Hook to use feature flags in components
 * This hook will re-render the component when the feature flag changes
 * 
 * @param flag The feature flag to check
 * @param userId Optional user ID for user-specific flags
 * @returns The feature flag value (true or false)
 */
export function useFeatureFlag(flag: FeatureFlag, userId?: string): boolean {
  const [value, setValue] = useState<boolean>(() => getFeatureFlag(flag, userId));
  
  useEffect(() => {
    // Function to check and update the flag value
    const checkFlag = () => {
      const newValue = getFeatureFlag(flag, userId);
      if (newValue !== value) {
        setValue(newValue);
      }
    };
    
    // Set up event listener for feature flag changes
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', checkFlag);
    }
    
    // Clean up event listener
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', checkFlag);
      }
    };
  }, [flag, userId, value]);
  
  return value;
}